import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/EmptyState";
import { ShareNetwork } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AdminReferrals() {
  const [refs, setRefs] = useState(null);
  const [kind, setKind] = useState("customer");
  const [s, setS] = useState(null);

  const loadRefs = (k) => api.get(`/admin/referrals?kind=${k}`).then((r) => setRefs(r.data));
  const loadSettings = () => api.get("/admin/settings").then((r) => setS(r.data));
  useEffect(() => { loadRefs(kind); }, [kind]);
  useEffect(() => { loadSettings(); }, []);

  const saveRewards = async () => {
    try {
      await api.put("/admin/settings", { settings: {
        referral_reward_enabled: !!s.referral_reward_enabled,
        referral_reward_amount: Number(s.referral_reward_amount) || 0,
        referral_reward_note: s.referral_reward_note || "",
        owner_referral_reward_enabled: !!s.owner_referral_reward_enabled,
        owner_referral_reward_amount: Number(s.owner_referral_reward_amount) || 0,
        owner_referral_reward_note: s.owner_referral_reward_note || "",
      }});
      toast.success("Reward settings saved");
      loadSettings();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setK = (k) => (e) => setS({ ...s, [k]: e?.target ? e.target.value : e });

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Referrals</h1>

      {s && (
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <p className="font-heading font-semibold">Customer referral reward</p>
            <div className="mt-3 flex items-center justify-between"><span className="text-sm text-stone-600">Enabled</span><Switch data-testid="cust-reward-toggle" checked={!!s.referral_reward_enabled} onCheckedChange={(v) => setK("referral_reward_enabled")(v)} /></div>
            <div className="mt-3"><Label className="text-xs text-stone-500">Amount ₹ (per successful referral)</Label><Input data-testid="cust-reward-amount" type="number" value={s.referral_reward_amount ?? 0} onChange={setK("referral_reward_amount")} className="mt-1 rounded-xl" /></div>
            <div className="mt-3"><Label className="text-xs text-stone-500">Note</Label><Input value={s.referral_reward_note ?? ""} onChange={setK("referral_reward_note")} className="mt-1 rounded-xl" placeholder="Credited after first booking" /></div>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <p className="font-heading font-semibold">Owner referral reward</p>
            <div className="mt-3 flex items-center justify-between"><span className="text-sm text-stone-600">Enabled</span><Switch data-testid="owner-reward-toggle" checked={!!s.owner_referral_reward_enabled} onCheckedChange={(v) => setK("owner_referral_reward_enabled")(v)} /></div>
            <div className="mt-3"><Label className="text-xs text-stone-500">Amount ₹</Label><Input type="number" value={s.owner_referral_reward_amount ?? 0} onChange={setK("owner_referral_reward_amount")} className="mt-1 rounded-xl" /></div>
            <div className="mt-3"><Label className="text-xs text-stone-500">Note</Label><Input value={s.owner_referral_reward_note ?? ""} onChange={setK("owner_referral_reward_note")} className="mt-1 rounded-xl" placeholder="Credited after property approval" /></div>
          </div>
        </div>
      )}
      {s && <Button data-testid="save-rewards" onClick={saveRewards} className="mt-4 rounded-xl bg-primary hover:bg-green-900">Save reward settings</Button>}
      <p className="mt-2 text-xs text-stone-400">Rewards are display-only for now — no automatic cash payout is made.</p>

      <div className="mt-6">
        <Tabs value={kind} onValueChange={setKind}>
          <TabsList><TabsTrigger value="customer" data-testid="tab-customer">Customer</TabsTrigger><TabsTrigger value="owner" data-testid="tab-owner">Owner</TabsTrigger></TabsList>
        </Tabs>
      </div>

      <div className="mt-4 space-y-2">
        {refs === null ? null : refs.length === 0 ? (
          <EmptyState icon={ShareNetwork} title="No referrals yet" message="Referrals appear here once users sign up via a referral link." />
        ) : refs.map((r) => (
          <div key={r.id} data-testid={`referral-${r.id}`} className="flex items-center justify-between rounded-2xl border border-stone-200 bg-white p-4">
            <div>
              <p className="text-sm font-medium text-stone-800">{r.referrer_name} <span className="text-stone-400">referred</span> {r.referred_name}</p>
              <p className="text-xs text-stone-400">Code {r.code} · {new Date(r.created_at).toLocaleDateString("en-IN")}</p>
            </div>
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">{r.status}</span>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
